<template>
  <div class="grid grid-cols-2 items-center gap-3 pl-5 pr-3">
    <!-- Asset -->
    <div class="flex items-center gap-2 min-w-0">
      <div class="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-md overflow-hidden bg-background-normal">
        <img
          v-if="previewUrl && !previewError"
          :src="previewUrl"
          class="w-full h-full object-cover"
          @error="previewError = true"
        >
        <VideoIcon v-else-if="upload.type === 'video'" :width="18" fill="currentColor" class="text-icon-normal" />
        <ImageIcon v-else :width="18" fill="currentColor" class="text-icon-normal" />
      </div>
      <div class="flex flex-col min-w-0 flex-grow">
        <BaseText type="body" size="sm" class="text-text-muted truncate">
          {{ upload.name }}
        </BaseText>

        <!-- Status -->
        <div v-if="upload.status === 'uploading'" class="flex items-center gap-2">
          <div class="flex-grow h-1 rounded-full bg-background-hover overflow-hidden">
            <div class="h-full rounded-full bg-primary-blue-100 transition-all duration-200" :style="{ width: `${upload.progress}%` }" />
          </div>
          <BaseText type="body" size="xs" class="text-text-subdued flex-shrink-0">
            {{ upload.progress }}%
          </BaseText>
        </div>
        <BaseText v-else-if="upload.status !== 'done'" type="body" size="xs" :class="upload.status === 'error' ? 'text-secondary-red-300' : 'text-text-subdued'">
          {{ statusText }}
        </BaseText>
      </div>
    </div>

    <!-- Board -->
    <div v-if="upload.status === 'done'">
      <div :ref="`file-dropdown-${upload.ad.id}`" class="rounded-lg border border-border-normal bg-white transition-colors hover:border-border-hover">
        <SaveAdDropdown
          :advertisement="upload.ad"
          :isSavedAd="true"
          :isEditMode="true"
          noHoverOpener
          @dropdownOpened="handleDropdownOpened"
          @dropdownClosed="handleDropdownClosed"
          @updateBoards="$emit('updateBoards', $event)"
        />
      </div>
      <div v-show="dropdownOpenedId === upload.ad.id" :ref="`file-dropdown-placeholder-${upload.ad.id}`" />
    </div>
    <div v-else-if="upload.status === 'error' && upload.error.retryable" class="flex justify-end">
      <BaseButton outlined @click="$emit('retry')">
        <div class="flex items-center gap-1">
          <RefreshIcon :width="16" :height="16" />
          Retry
        </div>
      </BaseButton>
    </div>
    <div v-else-if="upload.status === 'processing'" class="flex justify-end text-icon-normal">
      <BaseLoadingSpinnerCircle :width="18" :height="18" />
    </div>
  </div>
</template>

<script>
import SaveAdDropdown from '../SaveAdDropdown/SaveAdDropdown.vue'
import VideoIcon from '../../globals/Icons/VideoIcon.vue'
import ImageIcon from '../../globals/Icons/ImageIcon.vue'
import RefreshIcon from '../../globals/Icons/LensIcons/RefreshIcon.vue'

export default {
  name: 'File',
  components: {
    SaveAdDropdown,
    VideoIcon,
    ImageIcon,
    RefreshIcon
  },
  props: {
    // Upload entry from ManualUpload - { name, type, status, progress, error, ad }
    upload: {
      type: Object,
      required: true
    },
    dropdownOpenedId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      previewError: false
    }
  },
  computed: {
    previewUrl () {
      return this.upload.ad?.thumbnail || null
    },
    statusText () {
      switch (this.upload.status) {
        case 'queued': return 'Waiting to upload...'
        case 'processing': return 'Processing...'
        case 'error': return this.upload.error.message
        default: return ''
      }
    }
  },
  methods: {
    handleDropdownOpened () {
      // Handle set the dropdown to fixed position so it can overflow the scrollable container
      const dropdown = this.$refs[`file-dropdown-${this.upload.ad.id}`]
      const { top, left, width, height } = dropdown.getBoundingClientRect()
      dropdown.style.position = 'fixed'
      dropdown.style.top = `${top}px`
      dropdown.style.left = `${left}px`
      dropdown.style.width = `${width}px`

      // Set the height for a placeholder to prevent layout shifts
      const placeholder = this.$refs[`file-dropdown-placeholder-${this.upload.ad.id}`]
      placeholder.style.height = `${height}px`
      this.$emit('update:dropdownOpenedId', this.upload.ad.id) // Updating synced prop locks scrolling in parent container
    },
    handleDropdownClosed () {
      // Reset styling
      const dropdown = this.$refs[`file-dropdown-${this.upload.ad.id}`]
      dropdown.style.position = ''
      dropdown.style.top = ''
      dropdown.style.left = ''
      this.$emit('update:dropdownOpenedId', null) // Updating synced prop unlocks scrolling in parent container
    }
  }
}
</script>
